import { useState, useMemo, useEffect } from 'react';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Textarea from '@cloudscape-design/components/textarea';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import {
  detectInvisibleChars,
  removeInvisibleChars,
  getExampleText,
  formatUnicodeCode,
} from '@utils/invisibleCharacters';
import { analyzeSecurityRisks } from '@utils/securityAnalysis';
import { useClipboard } from '@hooks/useClipboard';
import { InvisibleCharacterAnalysis } from './InvisibleCharacterAnalysis';
import { AnalysisCache } from '@utils/debounce';
import { SecurityAnalysis } from '@utils/securityAnalysis';

// Type definition for window.loadSampleForTesting
declare global {
  interface Window {
    loadSampleForTesting?: (filename: string) => Promise<{success?: boolean; error?: string; file?: string; loaded?: number}>;
  }
}

// Global cache instance for analysis results (LRU with max 100 entries)
const analysisCache = new AnalysisCache();

// Global reference for test automation
let globalSetInputText: ((text: string) => void) | null = null;

// Test helper: Load a sample file and trigger analysis
// Usage: window.loadSampleForTesting('01-zero-width-space.txt')
if (typeof window !== 'undefined') {
  window.loadSampleForTesting = async (filename: string) => {
    try {
      const response = await fetch(`/samples/${filename}`);
      if (!response.ok) throw new Error(`Failed to load ${filename}`);
      const content = await response.text();
      
      if (globalSetInputText) {
        globalSetInputText(content);
        return { success: true, file: filename, loaded: content.length };
      }
      return { error: 'Component not ready' };
    } catch (error) {
      return { error: (error as Error).message };
    }
  };
}

export const HiddenCharactersTab = () => {
  const [inputText, setInputText] = useState('');
  const [showSpaces, setShowSpaces] = useState(false);
  const [showUnicode, setShowUnicode] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [highlightedPosition, setHighlightedPosition] = useState<number | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Store the setter globally for test automation
  useEffect(() => {
    globalSetInputText = setInputText;
    return () => {
      globalSetInputText = null;
    };
  }, []);
  
  // Use clipboard hook for cross-browser support
  const { pasteFromClipboard, copyToClipboard, error: clipboardError, clearError } = useClipboard();

  // Always detect invisible chars (for display purposes, respecting showSpaces checkbox)
  const detectedChars = useMemo(() => {
    return detectInvisibleChars(inputText, showSpaces);
  }, [inputText, showSpaces]);

  // Detect actual invisible chars (excluding spaces) for security analysis
  const actualInvisibleChars = useMemo(() => {
    return detectInvisibleChars(inputText, false); // Never include spaces for analysis
  }, [inputText]);

  // Security analysis with caching and performance optimization
  const securityAnalysis = useMemo(() => {
    if (!inputText || actualInvisibleChars.length === 0) {
      return null;
    }

    // Generate cache key
    const cacheKey = AnalysisCache.textHash(inputText);

    // Check cache first (avoid duplicate analysis)
    if (analysisCache.has(cacheKey)) {
      return analysisCache.get<SecurityAnalysis>(cacheKey);
    }

    // For large texts (>50KB), show loading state and debounce
    if (inputText.length > 50000) {
      setIsAnalyzing(true);
      
      // Defer analysis to prevent UI blocking
      setTimeout(() => {
        const analysis = analyzeSecurityRisks(inputText, actualInvisibleChars);
        analysisCache.set(cacheKey, analysis);
        setIsAnalyzing(false);
      }, 100);
      
      return null; // Return null while analyzing
    }

    // Normal analysis for smaller texts
    const analysis = analyzeSecurityRisks(inputText, actualInvisibleChars);
    analysisCache.set(cacheKey, analysis);
    return analysis;
  }, [inputText, actualInvisibleChars]);


  const handlePasteFromClipboard = async () => {
    const text = await pasteFromClipboard();
    if (text) {
      setInputText(text);
    }
  };

  const handleTryExample = () => {
    setInputText(getExampleText());
  };

  const handleClear = () => {
    setInputText('');
  };

  const handleRemoveInvisible = () => {
    const cleaned = removeInvisibleChars(inputText, true);
    setInputText(cleaned);
  };

  const handleCopyCleaned = async () => {
    const cleaned = removeInvisibleChars(inputText, true);
    const success = await copyToClipboard(cleaned);
    if (success) {
      setCopySuccess('Cleaned text copied to clipboard!');
      setTimeout(() => setCopySuccess(''), 3000);
    }
  };

  const handleFixCharacter = (charCode: number) => {
    // Remove all instances of this specific character
    const charToRemove = String.fromCharCode(charCode);
    const newText = inputText.split(charToRemove).join('');
    setInputText(newText);
    setCopySuccess(`Removed all instances of ${formatUnicodeCode(charCode)}`);
    setTimeout(() => setCopySuccess(''), 3000);
  };

  const handlePositionClick = (position: number) => {
          setHighlightedPosition(position);
    // Clear highlight after 3 seconds
    setTimeout(() => setHighlightedPosition(null), 3000);
  };


  return (
    <SpaceBetween size="l">
      {copySuccess && (
        <Alert key="success-alert" type="success" dismissible onDismiss={() => setCopySuccess('')}>
          {copySuccess}
        </Alert>
      )}

      {clipboardError && (
        <Alert key="clipboard-error-alert" type="warning" dismissible onDismiss={clearError}>
          {clipboardError}
        </Alert>
      )}

            <Container
        key="text-input-container"
              header={
                <Header
                  variant="h2"
                  description="Enter or paste your text to detect invisible characters"
                >
                  Text Input
                </Header>
              }
            >
              <SpaceBetween size="m">
                <Textarea
                  value={inputText}
                  onChange={({ detail }) => setInputText(detail.value)}
                  placeholder="Enter or paste your text here..."
                  rows={8}
                />
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={handlePasteFromClipboard}>Paste from Clipboard</Button>
                  <Button onClick={handleTryExample}>Try Example Text</Button>
                  <Button onClick={handleClear}>Clear</Button>
                  <div style={{ marginLeft: 'auto', fontSize: '13px', color: '#5f6b7a', fontStyle: 'italic' }}>
                    There are 21 more Example Texts with Security Patterns in the About Analyze text on invisible Characters - Tab.
                  </div>
                </SpaceBetween>
              </SpaceBetween>
            </Container>

            {inputText && (
        <>
          {isAnalyzing && (
            <Alert type="info" dismissible onDismiss={() => setIsAnalyzing(false)}>
              Analyzing large text ({inputText.length.toLocaleString()} characters)... This may take a moment.
            </Alert>
          )}
        <InvisibleCharacterAnalysis
          text={inputText}
          detectedChars={detectedChars}
          securityAnalysis={securityAnalysis}
          showSpaces={showSpaces}
          showUnicode={showUnicode}
          onShowSpacesChange={setShowSpaces}
          onShowUnicodeChange={setShowUnicode}
          showActionButtons={true}
          onRemoveInvisible={handleRemoveInvisible}
          onCopyCleaned={handleCopyCleaned}
          onCharacterClick={handlePositionClick}
          onCharacterFix={handleFixCharacter}
          highlightedPosition={highlightedPosition}
        />
        </>
      )}

    </SpaceBetween>
  );
};

